import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const FreeAssessmentsHub = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const assessments = [
    {
      id: 'data-maturity',
      title: 'Data Maturity Assessment',
      description: 'Evaluate your organization\'s data capabilities across infrastructure, analytics, governance, team skills, and strategic alignment.',
      icon: 'Database',
      color: 'from-blue-500 to-blue-600',
      benefits: [
        'Comprehensive 5-dimension evaluation',
        'Personalized improvement roadmap',
        'Benchmark against industry standards',
        'Downloadable PDF report'
      ],
      duration: '15-20 minutes',
      path: '/data-maturity-calculator-lead-generation-assessment',
      cta: 'Start Assessment'
    },
    {
      id: 'data-governance',
      title: 'Data Governance Diagnostic',
      description: 'Assess your organisation\'s data governance maturity across 6 core domains — from operating model to data lifecycle — and get a prioritised 90-day roadmap.',
      icon: 'ShieldCheck',
      color: 'from-[#002B45] to-[#1FB5A5]',
      benefits: [
        '6-domain governance maturity score',
        'Top 3 critical risks identified',
        'Quick wins and 90-day roadmap',
        'Instant results — no sign-up needed'
      ],
      duration: '15-20 minutes',
      path: '/data-governance-diagnostic-assessment',
      cta: 'Start Diagnostic'
    },
    {
      id: 'roi-calculator',
      title: 'ROI Calculator Suite',
      description: 'Calculate the financial impact of your data strategy initiatives with our interactive ROI calculators for compliance, analytics, and data strategy.',
      icon: 'TrendingUp',
      color: 'from-green-500 to-green-600',
      benefits: [
        'Multiple specialized calculators',
        'Quantify cost savings and revenue impact',
        'Compliance cost estimation',
        'Analytics readiness assessment'
      ],
      duration: '10-15 minutes',
      path: '/roi-calculator-suite-value-demonstration-tools',
      cta: 'Calculate ROI'
    },
    {
      id: 'ai-readiness',
      title: 'AI Readiness Assessment',
      description: 'Determine your organization\'s readiness to implement AI solutions by evaluating data infrastructure, technical capabilities, and organizational maturity.',
      icon: 'Sparkles',
      color: 'from-purple-500 to-purple-600',
      benefits: [
        'AI implementation readiness score',
        'Technology stack recommendations',
        'Risk assessment and mitigation',
        'Phased implementation roadmap'
      ],
      duration: '12-18 minutes',
      path: '/ai-solutions-intelligent-data-strategy-services',
      cta: 'Check AI Readiness'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Free Assessments - Data Maturity, ROI & AI Readiness | Northstar Analytics</title>
        <meta 
          name="description" 
          content="Access our suite of free assessment tools: Data Maturity Calculator, ROI Calculator Suite, and AI Readiness Assessment. Get personalized insights and actionable recommendations." 
        />
        <meta name="keywords" content="data maturity assessment, ROI calculator, AI readiness, free tools, data strategy, analytics assessment" />
        <meta property="og:title" content="Free Assessments - Data Maturity, ROI & AI Readiness | Northstar Analytics" />
        <meta property="og:description" content="Evaluate your data capabilities with our comprehensive suite of free assessment tools. Get instant insights and personalized recommendations." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="/free-assessments-hub" />
      </Helmet>
      
      <Header />
      
      <main className="py-16 px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="max-w-7xl mx-auto text-center mb-16">
          <div className="inline-flex items-center justify-center p-2 bg-secondary/10 rounded-full mb-6">
            <Icon name="Calculator" className="w-6 h-6 text-secondary mr-2" />
            <span className="text-sm font-semibold text-secondary uppercase tracking-wide">Free Assessment Tools</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary mb-6">
            Evaluate Your Data & AI Readiness
          </h1>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Get instant insights into your organization's data maturity, ROI potential, and AI readiness. 
            Our comprehensive assessment tools provide personalized recommendations to accelerate your data transformation journey.
          </p>
          
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-600">
            <div className="flex items-center">
              <Icon name="CheckCircle2" className="w-5 h-5 text-green-600 mr-2" />
              <span>100% Free - No Credit Card</span>
            </div>
            <div className="flex items-center">
              <Icon name="Clock" className="w-5 h-5 text-blue-600 mr-2" />
              <span>10-20 Minutes Each</span>
            </div>
            <div className="flex items-center">
              <Icon name="FileText" className="w-5 h-5 text-purple-600 mr-2" />
              <span>Instant Results & Reports</span>
            </div>
          </div>
        </div>

        {/* Assessment Cards */}
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-8 mb-16">
          {assessments?.map((assessment) => (
            <div 
              key={assessment?.id}
              className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group flex flex-col"
            >
              {/* Card Header with Gradient */}
              <div className={`bg-gradient-to-r ${assessment?.color} p-6 text-white`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-white/20 rounded-lg backdrop-blur-sm">
                    <Icon name={assessment?.icon} className="w-8 h-8" />
                  </div>
                  <span className="text-sm font-medium bg-white/20 px-3 py-1 rounded-full backdrop-blur-sm">
                    {assessment?.duration}
                  </span>
                </div>
                <h2 className="text-2xl font-bold mb-2">{assessment?.title}</h2>
                <p className="text-white/90 text-sm">{assessment?.description}</p>
              </div>

              {/* Card Body */}
              <div className="p-6 flex flex-col flex-1">
                <h3 className="text-sm font-semibold text-primary uppercase tracking-wide mb-4">What You'll Get:</h3>
                <ul className="space-y-3 mb-6 flex-1">
                  {assessment?.benefits?.map((benefit, index) => (
                    <li key={index} className="flex items-start">
                      <Icon name="Check" className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">{benefit}</span>
                    </li>
                  ))}
                </ul>

                <Link to={assessment?.path} className="block">
                  <Button 
                    className="w-full group-hover:scale-105 transition-transform duration-300"
                    size="lg"
                  >
                    {assessment?.cta}
                    <Icon name="ArrowRight" className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Why Use Our Assessments Section */}
        <div className="max-w-5xl mx-auto bg-gradient-to-br from-primary/5 to-secondary/5 rounded-3xl p-8 md:p-12">
          <h2 className="text-3xl font-bold text-primary text-center mb-8">Why Use Our Assessment Tools?</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="flex items-start">
              <div className="p-3 bg-blue-100 rounded-lg mr-4 flex-shrink-0">
                <Icon name="Target" className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-primary mb-2">Identify Gaps & Opportunities</h3>
                <p className="text-gray-600">Discover specific areas where your organization can improve and prioritize high-impact initiatives.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="p-3 bg-green-100 rounded-lg mr-4 flex-shrink-0">
                <Icon name="TrendingUp" className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-primary mb-2">Build Business Cases</h3>
                <p className="text-gray-600">Get quantifiable metrics and ROI projections to secure executive buy-in for data initiatives.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="p-3 bg-purple-100 rounded-lg mr-4 flex-shrink-0">
                <Icon name="Map" className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-primary mb-2">Get Actionable Roadmaps</h3>
                <p className="text-gray-600">Receive personalized implementation plans with prioritized steps and realistic timelines.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="p-3 bg-orange-100 rounded-lg mr-4 flex-shrink-0">
                <Icon name="Users" className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-primary mb-2">Expert-Backed Insights</h3>
                <p className="text-gray-600">Benefit from frameworks developed by data strategy experts with decades of experience.</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="max-w-4xl mx-auto text-center mt-16">
          <h2 className="text-3xl font-bold text-primary mb-4">Ready to Transform Your Data Strategy?</h2>
          <p className="text-lg text-gray-600 mb-8">
            Start with any assessment above, or schedule a consultation to discuss your specific needs.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact-multi-channel-engagement-hub">
              <Button size="lg" variant="primary">
                <Icon name="Calendar" className="w-5 h-5 mr-2" />
                Schedule Consultation
              </Button>
            </Link>
            <Link to="/services-overview-interactive-capability-showcase">
              <Button size="lg" variant="outline">
                Explore Our Services
                <Icon name="ArrowRight" className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default FreeAssessmentsHub;